// M3 megaprojects (M3_SPEC §12): long timers (rules.MEGAPROJECTS) that unlock a transport for their fields on the
// opening day; holds (TAPS: NEPA injunction 1970–73) delay the opening unless a consortium member with counsel
// lobbies 30% of the hold away; consortium buy-in (share of capex paid pro-rata over construction, overrun draw) for a
// tariff discount on own barrels and a share of all tariff income. state.m3.megaprojects[id] (lazy).
import { wellsOnField } from '../indexes.js';
import { normal } from '../../core/rng.js';
import { eqDef, fieldDef, stat, yf } from '../defs.js';
import { inflate } from '../../data/cpi.js';
import { MEGAPROJECTS, MEGAPROJECT_RULES } from '../../data/m3/rules.js';
import { m3, m3On } from './opsutil.js';
import { spend, earn, notify, fmt$ } from '../util.js';
import { m4MegaOpenDay, m4MegaName } from '../m4/worldHooks.js'; // M4-C

const ok = (msg, extra = {}) => ({ ok: true, msg, ...extra });
const no = (msg) => ({ ok: false, msg });
export const THROUGHPUT_SHARE = 0.5;      // average line fill after opening (share of capacity, est.)
export const COUNSEL_HOLD_CUT = 0.3;

const holdDays = (mp) => (mp.holds ?? []).reduce((a, h) => a + (h.until - h.from), 0);
const hasCounsel = (state) => Object.values(state.crew ?? {}).some((c) => c.status === 'active' && c.role === 'counsel');

export function mpState(state, id) {
  const M = m3(state);
  return (M.megaprojects[id] ??= { share: 0, paid: 0, committed: 0, joinDay: null, counsel: false, phase: 'planned', tariffIncome: 0 });
}

// Effective opening day: historical, minus the lobbied share of the holds when a member has counsel.
export function openDayOf(state, id) {
  const mp = MEGAPROJECTS[id];
  if (!mp) return m4MegaOpenDay(state, id) ?? Infinity; // M4-C: 2000s pipelines / LNG (MEGAPROJECTS_M4)
  const s = state.m3?.megaprojects?.[id];
  return mp.openDay - (s?.share > 0 && s.counsel ? Math.round(COUNSEL_HOLD_CUT * holdDays(mp)) : 0);
}
export const isOpen = (state, id) => state.day >= openDayOf(state, id);

function phaseAt(state, id) {
  const mp = MEGAPROJECTS[id];
  const d = state.day;
  if (d >= openDayOf(state, id)) return 'open';
  if (d < mp.startDay) return 'planned';
  const cut = state.m3?.megaprojects?.[id]?.counsel && state.m3.megaprojects[id].share > 0 ? COUNSEL_HOLD_CUT : 0;
  for (const h of mp.holds ?? []) if (d >= h.from && d < h.until - cut * (h.until - h.from)) return 'hold';
  return 'construction';
}

// Projects that gate a transport at a field: 'megaproject:<id>' requirements and the project's `unlocks`.
const PROJECT_FOR = new Map(); // `${fieldId}|${tdef.id}` -> id | null (static data; hot: every netback)
export function projectFor(fieldId, tdef) {
  const key = tdef?.id != null ? `${fieldId}|${tdef.id}` : null;
  if (key != null) { const v = PROJECT_FOR.get(key); if (v !== undefined) return v; }
  const v = projectForRaw(fieldId, tdef);
  if (key != null) PROJECT_FOR.set(key, v);
  return v;
}
function projectForRaw(fieldId, tdef) {
  const req = stat(tdef, 'requires', null);
  if (typeof req === 'string' && req.startsWith('megaproject:')) return req.slice(12);
  for (const [id, mp] of Object.entries(MEGAPROJECTS)) if (mp.fieldIds.includes(fieldId) && mp.unlocks.includes(tdef?.id)) return id;
  return null;
}

// Production/transport hook: true when the field's transport cannot move oil today (project not open, storm downtime).
export function m3TransportBlocked(state, fieldId, tdef) {
  if (!tdef || !m3On(state)) return false;
  const pid = projectFor(fieldId, tdef);
  if (pid && !isOpen(state, pid)) return true;
  if (stat(tdef, 'stormDowntime', false)) {
    const st = state.fields[fieldId]?.storm;
    if (st && state.day >= st.startDay && state.day <= st.untilDay) return true;
  }
  return false;
}

export function transportBlockReason(state, fieldId, tdef) {
  const pid = projectFor(fieldId, tdef);
  if (pid && !isOpen(state, pid)) return MEGAPROJECTS[pid] ? `${MEGAPROJECTS[pid].name} is not open yet (${phaseAt(state, pid)}).` : `${m4MegaName(pid) ?? pid} is not open yet.`; // M4-C: M4 projects
  return null;
}

// Consortium members pay (1 − share × discount) of the tariff on their own barrels.
export function m3TariffMult(state, fieldId, tdef) {
  if (!tdef || !state.m3?.megaprojects) return 1;
  const pid = projectFor(fieldId, tdef);
  const s = pid ? state.m3.megaprojects[pid] : null;
  return s?.share > 0 ? 1 - s.share * MEGAPROJECT_RULES.tariffDiscountPerShare : 1;
}

export function joinMegaprojectImpl(state, id, share) {
  const mp = MEGAPROJECTS[id];
  if (!mp) return no('No such project.');
  if (!m3On(state)) return no('Not before 1946.');
  if (state.day >= openDayOf(state, id)) return no(`${mp.name} is already open.`);
  const sh = Math.round(Number(share) * 1000) / 1000;
  if (!(sh >= MEGAPROJECT_RULES.minShare && sh <= MEGAPROJECT_RULES.maxShare)) return no(`Shares run ${MEGAPROJECT_RULES.minShare * 100}–${MEGAPROJECT_RULES.maxShare * 100}%.`);
  const s = mpState(state, id);
  if (s.share > 0) return no(`You already hold ${Math.round(s.share * 100)}% of ${mp.name}.`);
  const y = Math.min(yf(state), 2025);
  const overrun = Math.exp(normal(state, 0, MEGAPROJECT_RULES.overrunSigma));
  const total = inflate(mp.capex, mp.baseYear, y) * overrun * sh;
  const days = Math.max(30, openDayOf(state, id) - Math.max(state.day, mp.startDay));
  const first = total / days * 30;
  if (state.company.cash < first) return no(`Your first month's cash call is ${fmt$(first)}.`);
  s.share = sh; s.committed = Math.round(total); s.joinDay = state.day; s.overrun = Math.round(overrun * 100) / 100; s.counsel = hasCounsel(state);
  notify(state, 'megaproject', { id, phase: 'joined', share: sh, text: `Joined the ${mp.name} consortium with ${Math.round(sh * 100)}% — ${fmt$(total)} in cash calls through construction.` });
  return ok(`Joined ${mp.name} (${Math.round(sh * 100)}%): ${fmt$(total)} over ${days} days.`);
}

export function tickMegaprojects(state) {
  const M = m3(state);
  const y = Math.min(yf(state), 2025);
  for (const [id, mp] of Object.entries(MEGAPROJECTS)) {
    if (state.day < mp.startDay - 1 && !M.megaprojects[id]) continue;
    const s = M.megaprojects[id];
    const ph = phaseAt(state, id);
    const prev = s?.phase ?? (state.day > mp.startDay ? null : 'planned');
    if (ph !== prev) {
      const st = mpState(state, id);
      st.phase = ph;
      if (prev !== null) {
        const shipper = mp.fieldIds.some((f) => wellsOnField(state, f).some((w) => !w.ownerOperatorId));
        const txt = { construction: prev === 'hold' ? `${mp.name}: construction resumes.` : `${mp.name}: construction begins.`, hold: `${mp.name} on hold: ${mp.holds?.[0]?.reason ?? 'legal challenge'}.`, open: `${mp.name} opens — ${mp.fieldIds.map((f) => fieldDef(f)?.name).filter(Boolean).join(', ')} can ship.` }[ph];
        if (txt) notify(state, 'megaproject', { id, phase: ph === 'construction' && prev === 'hold' ? 'resumed' : ph === 'construction' ? 'start' : ph, shipper, member: (st.share ?? 0) > 0, press: ph === 'open' && (shipper || st.share > 0) ? 'megaprojectOpen' : null,
          pause: ph === 'open' && (shipper || st.share > 0), level: ph === 'open' ? 'major' : 'news', text: txt });
      }
    }
    if (!s?.share) continue;
    const openD = openDayOf(state, id);
    if (state.day < openD) {
      const left = s.committed - s.paid;
      if (left > 0) { const pay = Math.min(left, left / Math.max(1, openD - state.day)); spend(state, pay, 'capex', false); s.paid += pay; }
    } else {
      const ramp = Math.min(1, (state.day - openD) / 365);
      const inc = s.share * inflate(mp.tariffPerBbl, mp.baseYear, y) * mp.capacityBbld * THROUGHPUT_SHARE * ramp;
      if (inc > 0) { earn(state, inc, 'revenue'); s.tariffIncome += inc; }
    }
  }
}

export function megaprojectsView(state) {
  return Object.entries(MEGAPROJECTS).map(([id, mp]) => {
    const s = state.m3?.megaprojects?.[id];
    const y = Math.min(yf(state), 2025);
    return { id, name: mp.name, regionId: mp.regionId, fieldIds: mp.fieldIds, unlocks: mp.unlocks, phase: m3On(state) ? phaseAt(state, id) : 'planned', startDay: mp.startDay, openDay: openDayOf(state, id),
      historicalOpenDay: mp.openDay, holds: mp.holds ?? [], capex: inflate(mp.capex, mp.baseYear, y), tariffPerBbl: inflate(mp.tariffPerBbl, mp.baseYear, y), capacityBbld: mp.capacityBbld,
      share: s?.share ?? 0, paid: Math.round(s?.paid ?? 0), committed: s?.committed ?? 0, tariffIncome: Math.round(s?.tariffIncome ?? 0), canJoin: m3On(state) && state.day < openDayOf(state, id) && !(s?.share > 0),
      minShare: MEGAPROJECT_RULES.minShare, maxShare: MEGAPROJECT_RULES.maxShare, transport: mp.unlocks.map((t) => eqDef(t)?.name ?? t) };
  });
}
